import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { API } from '../App';

// Iter 209 — Keys whose value may be either a plain string or a per-locale
// `{en, es, nl, de, fr, ar}` dict. Kept in sync with the backend's
// `LOCALISED_TEXT_KEYS` so the hook can transparently pick the right
// language at render time.
const LOCALISED_KEYS = new Set([
  'foundingTitle', 'foundingTagline', 'foundingIncludesLine',
  'foundingFuturePricingNotice', 'trialDescription', 'affiliateExample',
]);

/**
 * usePricingConfig — Iter 208.
 *
 * Reads the platform-wide pricing/copy doc managed at /admin/pricing-config
 * and exposes it to every customer-visible surface so the team can change
 * pricing wording in ONE place instead of grepping the codebase.
 *
 * Sensible defaults are returned while the fetch is in-flight so the page
 * never flashes empty pricing text.
 */
const DEFAULTS = {
  currentMode: 'paid',
  foundingTitle: 'Founding Business Offer — FREE for 12 months',
  foundingTagline: 'Free for 12 months · No card required · Listing goes live once approved',
  foundingFreePeriodMonths: 12,
  foundingNoCardRequired: true,
  foundingIncludesLine: 'Full business profile, photos, contact details, events and offers included.',
  foundingFuturePricingNotice: 'Future pricing (if introduced) will be communicated well in advance.',
  paidPlans: [],
  currency: 'EUR',
  vatIncluded: true,
  trialEnabled: true,
  trialLengthDays: 60,
  trialDescription: '',
  affiliateExample: '',
};

// Tiny module-level cache so multiple components sharing the page only
// trigger one HTTP call. Refreshes when `bump()` is called from the admin
// form after a successful save.
let _cache = null;
let _cacheReady = false;
const _listeners = new Set();
function bump() { _cacheReady = false; _cache = null; _listeners.forEach((fn) => fn()); }

// Iter 209 — Localised text-field helper. If the value is a string we
// return it directly (legacy / single-language config). If it's a dict
// keyed by locale, we return the requested language with EN as a fallback
// so partial translations still render gracefully.
const SUPPORTED_LOCALES = ['en', 'es', 'nl', 'de', 'fr', 'ar'];

export function pickLocalised(value, lang) {
  if (value == null) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'object') {
    const code = (lang || 'en').slice(0, 2).toLowerCase();
    return value[code] || value.en || value[Object.keys(value)[0]] || '';
  }
  return String(value);
}

export { SUPPORTED_LOCALES };

export default function usePricingConfig() {
  const { i18n } = useTranslation();
  const lang = (i18n?.language || 'en').slice(0, 2).toLowerCase();
  const [raw, setRaw] = useState(_cache || DEFAULTS);
  const [loading, setLoading] = useState(!_cacheReady);

  useEffect(() => {
    const subscribe = () => setRaw(_cache || DEFAULTS);
    _listeners.add(subscribe);
    if (_cacheReady && _cache) {
      setRaw(_cache); setLoading(false);
      return () => _listeners.delete(subscribe);
    }
    let alive = true;
    (async () => {
      try {
        const r = await axios.get(`${API}/pricing-config`);
        if (!alive) return;
        _cache = { ...DEFAULTS, ...(r.data || {}) };
        _cacheReady = true;
        setRaw(_cache);
      } catch {
        if (!alive) return;
        _cache = DEFAULTS;
        _cacheReady = true;
        setRaw(DEFAULTS);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; _listeners.delete(subscribe); };
  }, []);

  // Project every localised text field down to the current language so the
  // consumer can write `pricing.foundingTitle` without thinking about i18n.
  const projected = { ...raw };
  for (const key of LOCALISED_KEYS) {
    projected[key] = pickLocalised(raw[key], lang);
  }
  // Paid-mode override: when the platform is in Paid mode, the founding-period
  // copy must not leak into customer-facing surfaces. New businesses see the
  // 60-day trial + prices instead. (Existing founders are handled per-account
  // in the dashboard components, which key off business.subscriptionPlan.)
  if (projected.currentMode === 'paid') {
    const L = (lang || 'en').slice(0, 2);
    const pick = (m) => m[L] || m.en;
    projected.foundingTitle = pick({
      en: 'Paid plans with a 60-day free trial',
      es: 'Planes de pago con 60 días de prueba gratis',
      de: 'Kostenpflichtige Pläne mit 60 Tagen Gratis-Test',
      nl: 'Betaalde plannen met 60 dagen gratis proef',
      fr: 'Formules payantes avec essai gratuit de 60 jours',
      ar: 'خطط مدفوعة مع تجربة مجانية 60 يوماً',
    });
    projected.foundingTagline = pick({
      en: 'Basic €1.99/mo · Business Plus €2.99/mo · Annual €29.99/yr — first 60 days free.',
      es: 'Basic 1,99 €/mes · Business Plus 2,99 €/mes · Anual 29,99 €/año — primeros 60 días gratis.',
      de: 'Basic 1,99 €/Mon. · Business Plus 2,99 €/Mon. · Jährlich 29,99 €/Jahr — erste 60 Tage gratis.',
      nl: 'Basic € 1,99/mnd · Business Plus € 2,99/mnd · Jaarlijks € 29,99/jr — eerste 60 dagen gratis.',
      fr: 'Basic 1,99 €/mois · Business Plus 2,99 €/mois · Annuel 29,99 €/an — 60 premiers jours gratuits.',
      ar: 'Basic 1.99€/شهر · Business Plus 2.99€/شهر · سنوي 29.99€/سنة — أول 60 يوماً مجاناً.',
    });
    projected.trialDescription = pick({
      en: 'Paid plans start with a 60-day free trial. A card is collected securely via Stripe at signup; your first payment is taken after 60 days unless you cancel. Free Directory stays free.',
      es: 'Los planes de pago empiezan con 60 días de prueba gratis. Se recoge una tarjeta de forma segura con Stripe al registrarse; el primer cobro es tras 60 días salvo que canceles. El Directorio Gratuito sigue siendo gratis.',
      de: 'Kostenpflichtige Pläne starten mit 60 Tagen Gratis-Test. Bei der Anmeldung wird über Stripe sicher eine Karte hinterlegt; die erste Zahlung erfolgt nach 60 Tagen, sofern nicht gekündigt. Das kostenlose Verzeichnis bleibt gratis.',
      nl: 'Betaalde plannen starten met 60 dagen gratis proef. Bij aanmelding wordt via Stripe veilig een kaart verzameld; de eerste betaling is na 60 dagen tenzij u opzegt. Gratis Vermelding blijft gratis.',
      fr: 'Les formules payantes débutent par un essai gratuit de 60 jours. Une carte est collectée en sécurité via Stripe à l’inscription ; le premier paiement a lieu après 60 jours sauf annulation. L’Annuaire Gratuit reste gratuit.',
      ar: 'تبدأ الخطط المدفوعة بتجربة مجانية 60 يوماً. تُجمع بطاقة بأمان عبر Stripe عند التسجيل؛ ويُجرى الدفع الأول بعد 60 يوماً ما لم تُلغِ. يبقى الدليل المجاني مجانياً.',
    });
    projected.foundingNoCardRequired = false;
    projected.foundingIncludesLine = projected.trialDescription;
    projected.foundingFuturePricingNotice = '';
    projected.affiliateExample = pick({
      en: 'Example: a business you referred takes Business Plus at €2.99/month. After its 60-day free trial, each payment of €2.99 is €2.47 excluding IVA, so you earn about €1.24 (50% excluding IVA) on their first charge and on every renewal, for as long as they stay.',
      es: 'Ejemplo: una empresa que recomiendas contrata Business Plus por 2,99 €/mes. Tras su prueba gratuita de 60 días, cada pago de 2,99 € equivale a 2,47 € sin IVA, por lo que ganas unos 1,24 € (50% sin IVA) en su primer cobro y en cada renovación, mientras siga activa.',
      de: 'Beispiel: Ein von Ihnen geworbenes Unternehmen wählt Business Plus für 2,99 €/Monat. Nach der 60-tägigen Gratis-Testphase entsprechen 2,99 € einem Betrag von 2,47 € ohne MwSt., sodass Sie bei der ersten Zahlung und jeder Verlängerung rund 1,24 € (50% ohne MwSt.) verdienen, solange das Unternehmen bleibt.',
      nl: 'Voorbeeld: een bedrijf dat u aanbracht neemt Business Plus voor € 2,99/maand. Na de gratis proef van 60 dagen is elke betaling van € 2,99 gelijk aan € 2,47 excl. btw, dus verdient u ongeveer € 1,24 (50% excl. btw) bij de eerste betaling en elke verlenging, zolang zij blijven.',
      fr: 'Exemple : une entreprise que vous avez recommandée souscrit Business Plus à 2,99 €/mois. Après son essai gratuit de 60 jours, chaque paiement de 2,99 € représente 2,47 € hors TVA, vous gagnez donc environ 1,24 € (50% hors TVA) sur son premier paiement et à chaque renouvellement, tant qu\'elle reste active.',
      ar: 'مثال: عمل أوصيتَ به يشترك في Business Plus بسعر 2.99€/شهر. بعد تجربته المجانية لمدة 60 يوماً، تعادل كل دفعة 2.99€ مبلغ 2.47€ بدون ضريبة القيمة المضافة، فتكسب نحو 1.24€ (50% بدون الضريبة) على أول دفعة وعلى كل تجديد، طالما بقي مشتركاً.',
    });
  }
  // Always expose the raw doc too — the admin form needs the full
  // language map to render its tabs.
  projected._raw = raw;
  projected._lang = lang;
  projected.loading = loading;
  return projected;
}

export function invalidatePricingConfigCache() { bump(); }
